// Implements: F-UIX-014
/**
 * The `next` parameter of `/login?next=…`, validated twice.
 *
 * An open redirect is the threat this file exists for (design section 13): the
 * value decides where somebody lands after signing in, and it arrives in a URL
 * anyone can write. It is validated when it is WRITTEN and again when it is
 * CONSUMED, because between those two moments it lives in the address bar,
 * where nothing this application controls can protect it.
 *
 * The rule, from design DD6: the value starts with `/`, does not start with
 * `//` or `/\`, contains no backslash, and decodes to itself. Everything else
 * becomes `/`. Requiring the leading slash is also what removes the "no colon
 * before the first slash" case: a value that starts with `/` cannot carry a
 * scheme, so `https://evil.com` and `javascript:alert(1)` are rejected by the
 * same line.
 */

/** Where an invalid or missing `next` sends: the application root. */
export const SAFE_NEXT = "/";

/** The route the interceptor and the guard redirect to. */
export const LOGIN_ROUTE = "/login";

/** The query parameter that carries the target. */
export const NEXT_PARAM = "next";

// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/u;

/**
 * The internal path `raw` names, or `/` when it names anything else.
 *
 * @param raw The candidate, already decoded once by whoever read it.
 */
export function validateNext(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") return SAFE_NEXT;
  if (!raw.startsWith("/")) return SAFE_NEXT;
  // `//evil.com` is a protocol-relative URL and `/\evil.com` is the same thing
  // to every browser that normalises the backslash.
  if (raw.startsWith("//") || raw.startsWith("/\\")) return SAFE_NEXT;
  if (raw.includes("\\")) return SAFE_NEXT;
  // A browser strips tab and line breaks from a URL before it parses it, so `/\t/evil.com` would
  // become `//evil.com` on navigation: no C0 control character and no DEL is a path anybody meant.
  if (CONTROL_CHARACTER.test(raw)) return SAFE_NEXT;

  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // A malformed escape is not a path anybody meant to reach.
    return SAFE_NEXT;
  }
  // `%2f%2fevil.com` survives the checks above and becomes `//evil.com` the
  // moment something decodes it, so a value that is not its own decoding is
  // rejected instead of being decoded here and re-examined.
  if (decoded !== raw) return SAFE_NEXT;

  return raw;
}

/**
 * The login URL that remembers where the caller was going.
 *
 * The parameter is ALWAYS written, `/` included. Omitting it when the target
 * happens to be the root would be a shorter URL and a second rule, and the
 * capability states one: the redirect carries the path plus query the caller
 * was on. A hostile value is not omitted either — it is replaced by `/`, so
 * what ends up in the address bar is a target that was validated rather than
 * the absence of one.
 *
 * @param target The path plus query the caller was trying to reach.
 */
export function loginPathWithNext(target: string | null | undefined): string {
  return `${LOGIN_ROUTE}?${NEXT_PARAM}=${encodeURIComponent(validateNext(target))}`;
}

/**
 * The validated `next` of a query string, for the side that consumes it.
 *
 * @param search The `location.search` of the login route, `?` included.
 */
export function readNextParam(search: string): string {
  return validateNext(new URLSearchParams(search).get(NEXT_PARAM));
}
